import {
  Autocomplete,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material'
import { type SyntheticEvent, useMemo, useState } from 'react'
import type { Schema } from 'yup'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  selectDisabledOptionIdsByFieldName,
  selectField,
} from '../../selectors'
import { setValue } from '../../slice.ts'
import type { DropDownValue, FieldName, SelectOption } from '../../types'
import fieldValidations from '../../validation.ts'

type DropDownProps = {
  name: FieldName
}

export default function DropDown(props: DropDownProps) {
  const { name } = props

  const { field, value, options = [] } = useAppSelector(selectField(name))
  const disabledOptionIds = useAppSelector(
    selectDisabledOptionIdsByFieldName(name),
  )
  const sortedOptions = useMemo(
    () =>
      [...options].sort((a, b) => (a?.description < b?.description ? -1 : 1)),
    [options],
  )
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const validations = fieldValidations[name] || []

  const dispatch = useAppDispatch()

  const { name: fieldName, label } = field

  const isRequiredCharacter =
    field.validations && field?.validations?.find((v) => v.type === 'required')
      ? '*'
      : ''

  const attributes = {
    name,
    value: value as DropDownValue,
    options: sortedOptions,
  }

  function handleChange(_event: SyntheticEvent, value: DropDownValue) {
    function validate() {
      setErrorMessages([])
      const errors: string[] = []
      const funcs = validations.map((func: Schema) => func.validate(value))
      Promise.all(funcs)
        .then(() => {
          setErrorMessages([])
          dispatch(setValue({ fieldName, value }))
        })
        .catch((error) => {
          errors.push(error.message)
          dispatch(setValue({ fieldName, value }))
        })

      setErrorMessages(errors)
    }
    validate()
  }

  return (
    <>
      <Autocomplete
        key={name}
        {...attributes}
        getOptionLabel={(option: SelectOption) => {
          if (option.description) return option.description
          return 'Unknown'
        }}
        getOptionDisabled={(option) =>
          disabledOptionIds?.includes(option.code) || false
        }
        onChange={handleChange}
        renderInput={(params) => (
          <TextField label={label + isRequiredCharacter} {...params} />
        )}
      />
      <Typography variant="caption">Select an option.</Typography>
      <FormHelperText>{errorMessages}</FormHelperText>
    </>
  )
}
